/**
 * L'interruttore a scorrimento dell'app, in un posto solo.
 *
 * Non e' uno slider nuovo: e' QUELLO che esisteva gia', scritto per le
 * preferenze del profilo (`PreferencesPanel.css`, «Toggle switch — forest
 * accent when on») e ripetuto a mano in nove punti. Il modale di creazione
 * torneo ne voleva un decimo, e ricopiarlo una volta di piu' avrebbe messo due
 * definizioni dello stesso comando in due fogli diversi — che e' esattamente
 * come nasce il tipo di bug che questa casa insegue da tempo.
 *
 * Le classi restano `settings-toggle` / `settings-toggle-slider`: sono quelle
 * che `ProfilePage.css` sovrascrive (`.profile-wrap .settings-panel
 * .settings-toggle { flex-shrink: 0 }`), e rinominarle vorrebbe dire inseguire
 * quell'override e i suoi fratelli per nessun guadagno. Cambia dove il foglio
 * VIVE — `ToggleSwitch.css` — non come si chiama. E' il secondo trasloco di
 * questo blocco: il primo lo racconta `ProfilePage.css:2649`.
 *
 * ── `wrapper`, e perche' esiste ─────────────────────────────────────────────
 *
 * L'input e' invisibile (`opacity: 0; width: 0`), quindi il clic non lo
 * raggiunge mai: arriva sul cursore disegnato, e vale solo se un `<label>` lo
 * sta avvolgendo o lo nomina. I due usi dell'app chiedono due strade opposte:
 *
 *  - nel profilo l'interruttore E' l'etichetta cliccabile (il testo a sinistra
 *    e' un fratello, non un `<label>`): serve `wrapper="label"`, il difetto;
 *  - nel modale del torneo la riga porta gia' un `<label htmlFor>` — il TESTO,
 *    non la riga intera — quindi qui il guscio dev'essere uno `<span>`: due
 *    `<label>` annidati sono HTML illecito, e `getByLabelText` smetterebbe di
 *    trovare il comando.
 *
 * ── Il cursore, quando il guscio e' uno `<span>` ────────────────────────────
 *
 * Qui stava il difetto, ed e' durato finche' il commento qui sopra ha detto
 * che nel modale «la riga INTERA e' un'etichetta»: non lo e'. La riga e' un
 * `<div>` (accanto al testo ci va un bottone d'aiuto, e un bottone dentro
 * un'etichetta e' HTML illecito), l'etichetta e' il solo testo a sinistra, e
 * col guscio a `<span>` il cursore disegnato non era associato a NIENTE —
 * dichiarava `cursor: pointer` e non rispondeva. Toccare il cursore e' pero'
 * il gesto che si fa.
 *
 * Quindi col guscio a `<span>` il cursore e' una SECONDA etichetta dello
 * stesso comando (`htmlFor={id}`). Due etichette per un solo controllo sono
 * HTML lecito, si concatenano nel nome accessibile — e la seconda e' vuota,
 * quindi il nome resta quello del testo — e portano gratis il `disabled` e il
 * fuoco che un `onClick` a mano avrebbe dovuto rifare.
 */

import './ToggleSwitch.css';

interface Props {
  /** Serve quando un `<label htmlFor>` esterno possiede il comando. */
  id?: string;
  checked: boolean;
  onChange: (valore: boolean) => void;
  disabled?: boolean;
  /** Solo per l'uso senza etichetta esterna. */
  ariaLabel?: string;
  /** L'id della nota che spiega il comando, letta dietro all'etichetta. */
  ariaDescribedBy?: string;
  /** Vedi il saggio qui sopra: `label` di suo, `span` dentro un'etichetta. */
  wrapper?: 'label' | 'span';
}

export default function ToggleSwitch({
  id,
  checked,
  onChange,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  wrapper = 'label',
}: Props) {
  const Guscio = wrapper;
  /*
   * Dentro un'etichetta il cursore resta uno `<span>`: annidarne due sarebbe
   * HTML illecito. Fuori — ed e' il caso del modale del torneo — diventa lui
   * l'etichetta che rende cliccabile cio' che si vede. Vedi il saggio in testa.
   */
  const Cursore = wrapper === 'span' && id ? 'label' : 'span';
  return (
    <Guscio className="settings-toggle">
      <input
        type="checkbox"
        {...(id ? { id } : {})}
        checked={checked}
        disabled={disabled}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        {...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Cursore
        className="settings-toggle-slider"
        {...(Cursore === 'label' ? { htmlFor: id } : {})}
      />
    </Guscio>
  );
}
